import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description, true);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('twitter:image', ogImage, true);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${canonical}`);
    }

    // Update OG URL
    const currentPath = window.location.pathname;
    updateMetaTag('og:url', `${siteUrl}${currentPath}`, true);
    updateMetaTag('twitter:url', `${siteUrl}${currentPath}`, true);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);
}

// Schema.org generators
export const generateRestaurantSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: 'Le Bokal Givré',
    image: 'https://readdy.ai/api/search-image?query=tropical%20brunch%20buffet%20restaurant%20la%20reunion%20colorful%20food&width=1200&height=630&seq=schema-image&orientation=landscape',
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+262693527676',
    email: 'lebokalgivre.cie@gmail.com',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '52 Chemin Edward Savigny, Ligne des Bambous',
      addressLocality: 'Saint-Pierre',
      postalCode: '97432',
      addressRegion: 'La Réunion',
      addressCountry: 'RE'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -21.3389,
      longitude: 55.4789
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Monday',
        opens: '10:00',
        closes: '15:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Monday',
        opens: '19:00',
        closes: '22:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Thursday',
        opens: '19:00',
        closes: '22:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '10:00',
        closes: '12:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '19:00',
        closes: '23:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '10:00',
        closes: '12:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '13:00',
        closes: '15:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '19:00',
        closes: '23:00'
      }
    ],
    servesCuisine: ['Cuisine créole', 'Tapas', 'Brunch', 'Cuisine internationale'],
    menu: `${siteUrl}#brunch`,
    acceptsReservations: 'True',
    sameAs: [
      'https://www.instagram.com/lebokalgivre/',
      'https://www.facebook.com/100063594639194'
    ]
  };
};

export const generateFAQSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Quels sont les horaires du brunch au Bokal Givré ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Le brunch buffet à volonté est servi du vendredi au lundi, de 10h à 15h. Le weekend (samedi et dimanche), nous proposons deux services : 10h-12h et 13h-15h.'
        }
      },
      {
        '@type': 'Question',
        name: 'Où se trouve Le Bokal Givré ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Le Bokal Givré est situé au 52 Chemin Edward Savigny, Ligne des Bambous, 97432 Saint-Pierre, La Réunion.'
        }
      },
      {
        '@type': 'Question',
        name: 'Comment réserver une table au Bokal Givré ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vous pouvez réserver par téléphone au +262 693 52 76 76 ou par email à lebokalgivre.cie@gmail.com.'
        }
      },
      {
        '@type': 'Question',
        name: 'Quel type de cuisine propose Le Bokal Givré ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Le Bokal Givré propose une cuisine métissée 100% maison : brunch buffet à volonté sucré et salé, tapas, et bar tropical. Notre cuisine mélange saveurs locales créoles et influences internationales.'
        }
      },
      {
        '@type': 'Question',
        name: 'Le Bokal Givré est-il ouvert le soir ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Oui, nous sommes ouverts le soir : lundi et jeudi de 19h à 22h, vendredi de 19h à 23h, et samedi-dimanche de 19h à 23h.'
        }
      }
    ]
  };
};

export const generateWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Le Bokal Givré - Brunch Buffet, Restaurant & Bar Tapas Saint-Pierre La Réunion',
    description: 'Le Bokal Givré à Saint-Pierre, La Réunion : Brunch buffet à volonté sucré & salé 100% maison, restaurant tapas et bar tropical. Cuisine métissée dans un cadre verdoyant.',
    url: siteUrl,
    inLanguage: 'fr-RE',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Le Bokal Givré',
      url: siteUrl
    }
  };
};
